/**
 * Live product mockup compositor.
 * Swaps artwork/frame/mat from Size · Frame · Mat option changes.
 * Frame moulding is a 0.75in padding band painted with a tiled finish chip.
 */

const SIZE_KEYS = ['12x18', '18x24', '24x36', '30x40', '30x60'];
const SIZE_INCHES = {
  '12x18': [12, 18],
  '18x24': [18, 24],
  '24x36': [24, 36],
  '30x40': [30, 40],
  '30x60': [30, 60],
};
const FRAME_CAPTION_LABELS = {
  white: 'Slim White Frame',
  black: 'Slim Black Frame',
  wood: 'Natural Wood Frame',
  red: 'Red Lacquered Frame',
};
const MAT_IN = 1.5;
const FRAME_IN = 0.75;
const LOAD_TIMEOUT_MS = 8000;
const REVEAL_TIMEOUT_MS = 2500;

/**
 * Decoded frame chips per URL, shared by every mockup on the page. Failed loads are evicted so they retry.
 * @type {Map<string, Promise<HTMLImageElement | null>>}
 */
const chipCache = new Map();

/**
 * Horizontal rail tiles per chip URL, shared by every mockup on the page. Failed builds are evicted so they retry.
 * @type {Map<string, Promise<{ top: string, bottom: string, images: HTMLImageElement[] } | null>>}
 */
const railTileCache = new Map();

/**
 * @param {string | null | undefined} url
 * @returns {string}
 */
function absoluteUrl(url) {
  if (!url) return '';
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;
  return url;
}

/**
 * iOS Safari can reject decode() on an image that loaded fine, so a decode failure after load still counts.
 * @param {string} url
 * @param {boolean} crossOrigin
 * @returns {Promise<HTMLImageElement | null>}
 */
function loadImageOnce(url, crossOrigin) {
  return new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    let settled = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;
    /** @param {boolean} loaded */
    const finish = (loaded) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (!loaded || !img.naturalWidth) {
        resolve(null);
        return;
      }
      const decoded = typeof img.decode === 'function' ? img.decode().catch(() => {}) : Promise.resolve();
      decoded.then(() => resolve(img));
    };
    timer = setTimeout(() => finish(img.complete && img.naturalWidth > 0), LOAD_TIMEOUT_MS);
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
  });
}

/**
 * @param {string} url
 * @param {{ crossOrigin?: boolean }} [options]
 * @returns {Promise<HTMLImageElement | null>}
 */
async function loadImage(url, { crossOrigin = true } = {}) {
  if (!url) return null;
  return (await loadImageOnce(url, crossOrigin)) || loadImageOnce(url, crossOrigin);
}

/**
 * @template T
 * @param {Map<string, Promise<T | null>>} cache
 * @param {string} key
 * @param {() => Promise<T | null>} factory
 * @returns {Promise<T | null>}
 */
function cachedOrRetry(cache, key, factory) {
  const cached = cache.get(key);
  if (cached) return cached;
  const promise = factory().catch(() => null);
  cache.set(key, promise);
  promise.then((value) => {
    if (!value && cache.get(key) === promise) cache.delete(key);
  });
  return promise;
}

/**
 * @param {string} url
 * @returns {Promise<HTMLImageElement | null>}
 */
function loadChip(url) {
  return cachedOrRetry(chipCache, url, () => loadImage(url));
}

/**
 * @param {HTMLImageElement} img
 * @param {number} angle
 * @returns {Promise<string | null>}
 */
function rotateChip(img, angle) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalHeight;
  canvas.height = img.naturalWidth;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(angle);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : null), 'image/jpeg', 0.92);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Snapshot capture drops rotated pseudo-elements, so top/bottom rails need a pre-rotated chip.
 * Tiles are decoded before resolving so swapping them in never paints an empty rail.
 * @param {string} url
 * @returns {Promise<{ top: string, bottom: string, images: HTMLImageElement[] } | null>}
 */
function loadRailTiles(url) {
  return cachedOrRetry(railTileCache, url, async () => {
    const img = await loadChip(url);
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;

    const [top, bottom] = await Promise.all([rotateChip(img, Math.PI / 2), rotateChip(img, -Math.PI / 2)]);
    const revoke = () => {
      if (top) URL.revokeObjectURL(top);
      if (bottom) URL.revokeObjectURL(bottom);
    };
    if (!top || !bottom) {
      revoke();
      return null;
    }

    const images = await Promise.all([loadImage(top, { crossOrigin: false }), loadImage(bottom, { crossOrigin: false })]);
    if (!images[0] || !images[1]) {
      revoke();
      return null;
    }
    return { top, bottom, images: /** @type {HTMLImageElement[]} */ (images) };
  });
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeOption(value) {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/["'″′]/g, '')
    .replace(/×/g, 'x')
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9x]/g, '');
}

/**
 * @param {string | null | undefined} sizeValue
 * @returns {string | null}
 */
function matchSizeKey(sizeValue) {
  const normalized = normalizeOption(sizeValue);
  if (!normalized) return null;

  for (const key of SIZE_KEYS) {
    if (normalized === key || normalized.includes(key)) return key;
  }

  const match = normalized.match(/(\d+)\s*x\s*(\d+)/);
  if (match) {
    const key = `${match[1]}x${match[2]}`;
    if (SIZE_KEYS.includes(key)) return key;
  }

  return null;
}

/**
 * @param {string | null | undefined} frameValue
 * @returns {string}
 */
function matchFrameKey(frameValue) {
  const normalized = normalizeOption(frameValue);
  if (!normalized) return 'unframed';
  if (normalized.includes('unframed') || normalized === 'none') return 'unframed';
  if (normalized.includes('white')) return 'white';
  if (normalized.includes('black')) return 'black';
  if (normalized.includes('wood') || normalized.includes('natural')) return 'wood';
  if (normalized.includes('red')) return 'red';
  return normalized;
}

/**
 * @param {string | null | undefined} matValue
 * @returns {boolean}
 */
function isMatOn(matValue) {
  const normalized = normalizeOption(matValue);
  if (!normalized) return false;
  if (normalized.includes('no') || normalized === 'false' || normalized === 'none') return false;
  if (normalized.includes('yes') || normalized === 'true' || normalized.includes('with')) return true;
  return false;
}

class LiveProductMockup extends HTMLElement {
  /** @type {any} */
  #config = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #frameIn = 0;

  /** @type {number} */
  #outerW = 1;

  /** @type {number} */
  #readyToken = 0;

  /** @type {number} */
  #frameToken = 0;

  /** @type {string | null} */
  #frameUrl = null;

  /** @type {Promise<boolean>} */
  #frameReady = Promise.resolve(true);

  /**
   * Resolves once the current frame is applied: `true` when every side can be captured,
   * `false` when the top/bottom rails rely on the rotated pseudo-element fallback.
   * @returns {Promise<boolean>}
   */
  whenRailsReady() {
    return this.#frameReady;
  }

  get #listenGlobal() {
    return this.hasAttribute('data-listen-global');
  }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#config = this.#readConfig();
    if (!this.#config) return;

    this.#preloadAllImages();
    this.#render(this.#config.initial || {});
    this.#observeShell();

    const section = this.closest('.shopify-section');
    const optionRoot = this.#listenGlobal ? document : section || document;

    optionRoot.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    optionRoot.addEventListener('variant:selected', this.#onVariantUpdate, { signal });
    optionRoot.addEventListener('change', this.#onOptionChange, { signal });
    document.addEventListener('change', this.#onOptionChange, { capture: true, signal });
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    document.addEventListener('variant:selected', this.#onVariantUpdate, { signal });

    this.#syncFromDom();
    queueMicrotask(() => this.#syncFromDom());
    requestAnimationFrame(() => this.#syncFromDom());
  }

  relayout() {
    this.#observeShell();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #observeShell() {
    const shell = this.querySelector('[data-live-mockup-frame-shell]');
    if (!(shell instanceof HTMLElement)) return;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#syncFramePx(shell));
    this.#resizeObserver.observe(shell);
    this.#syncFramePx(shell);
  }

  #preloadAllImages() {
    for (const url of Object.values(this.#config?.artworkBySize || {})) {
      if (!url) continue;
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = absoluteUrl(url);
    }

    const chips = (Array.isArray(this.#config?.frames) ? this.#config.frames : [])
      .map((frame) => absoluteUrl(frame?.overlay))
      .filter(Boolean);
    // Building every frame's rail tiles up front keeps later Frame switches instant.
    for (const url of chips) loadRailTiles(url);
  }

  /**
   * @param {HTMLImageElement} img
   * @returns {Promise<void>}
   */
  #whenDecoded(img) {
    if (!img?.src) return Promise.resolve();
    const finish = () => (typeof img.decode === 'function' ? img.decode().then(() => {}, () => {}) : Promise.resolve());
    if (img.complete && img.naturalWidth > 0) return finish();
    return new Promise((resolve) => {
      const done = () => {
        finish().then(resolve, resolve);
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  /**
   * Shows the stage once the art and frame are both painted, or after a timeout so it never stays hidden.
   * @param {HTMLImageElement} artEl
   */
  #revealWhenReady(artEl) {
    if (this.hasAttribute('data-ready')) return;
    const token = ++this.#readyToken;
    const ready = Promise.all([this.#whenDecoded(artEl), this.#frameReady]);
    const timeout = new Promise((resolve) => setTimeout(resolve, REVEAL_TIMEOUT_MS));
    Promise.race([ready, timeout]).then(() => {
      if (token !== this.#readyToken) return;
      this.setAttribute('data-ready', '');
    });
  }

  /**
   * @param {HTMLElement} shell
   */
  #syncFramePx(shell) {
    const width = shell.getBoundingClientRect().width;
    const px = this.#outerW > 0 ? (width * this.#frameIn) / this.#outerW : 0;
    this.style.setProperty('--frame-width', `${px}px`);
    // Pixel length for the rotated-pseudo rail fallback; the lightbox snapshot can't resolve container units.
    this.style.setProperty('--frame-rail-length', `${shell.offsetWidth || width}px`);
  }

  #readConfig() {
    const script = this.querySelector('[data-live-mockup-config]');
    if (!(script instanceof HTMLScriptElement)) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  }

  /**
   * @param {Event} event
   */
  #onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    const inPicker = Boolean(
      target.closest('variant-picker, .mobile-buybox, .product-information, [data-mobile-buybox]')
    );
    if (!/option/i.test(target.name || '') && !inPicker) return;

    queueMicrotask(() => this.#syncFromDom());
  };

  #onVariantUpdate = () => {
    queueMicrotask(() => this.#syncFromDom());
  };

  #optionRoot() {
    const frameFieldset = document.querySelector('.product-information [data-option-type="frame"], [data-option-type="frame"]');
    if (frameFieldset) {
      return (
        frameFieldset.closest('.product-information') ||
        frameFieldset.closest('form') ||
        document
      );
    }
    return (
      this.closest('.product-information') ||
      document.querySelector('.product-information--product-v2, .product-information--mobile-buybox') ||
      document
    );
  }

  #syncFromDom() {
    const root = this.#optionRoot();
    const sizeInput = document.querySelector('.product-information [data-option-type="size"] input:checked');
    const frameInput = document.querySelector('.product-information [data-option-type="frame"] input:checked');
    const matInput = document.querySelector('.product-information [data-option-type="mat"] input:checked');
    this.#render({
      size: sizeInput ? this.#inputLabel(sizeInput) : this.#readSelectedOption(root, ['size']),
      frame: frameInput ? this.#inputLabel(frameInput) : this.#readSelectedOption(root, ['frame', 'fram']),
      mat: matInput ? this.#inputLabel(matInput) : this.#readSelectedOption(root, ['mat']),
    });
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  #inputLabel(input) {
    return (
      input.getAttribute('aria-label') ||
      input.dataset.frameDisplayName ||
      input.value ||
      ''
    );
  }

  /**
   * @param {ParentNode} root
   * @param {string[]} needles
   */
  #readSelectedOption(root, needles) {
    for (const needle of needles) {
      const typedLists = root.querySelectorAll(`fieldset[data-option-type="${needle}"]`);
      for (const typed of typedLists) {
        const checked = typed.querySelector('input:checked');
        if (checked instanceof HTMLInputElement) return this.#inputLabel(checked);
        const select = typed.querySelector('select');
        if (select instanceof HTMLSelectElement && select.value) return select.value;
      }
    }

    const fieldsets = root.querySelectorAll('fieldset.variant-option, fieldset[data-option-name]');
    for (const fieldset of fieldsets) {
      const name =
        fieldset.getAttribute('data-option-name') ||
        fieldset.getAttribute('data-option-type') ||
        fieldset.querySelector('legend')?.textContent ||
        '';
      const handle = normalizeOption(name);
      if (!needles.some((n) => handle.includes(n))) continue;

      const checked = fieldset.querySelector('input:checked');
      if (checked instanceof HTMLInputElement) return this.#inputLabel(checked);
    }

    return this.dataset[needles[0]] || '';
  }

  /**
   * @param {{size?: string, frame?: string, mat?: string}} selection
   */
  #render(selection) {
    if (!this.#config) return;

    let sizeKey = matchSizeKey(selection.size) || matchSizeKey(this.dataset.size);
    const frameValue =
      selection.frame != null && String(selection.frame).trim() !== ''
        ? selection.frame
        : this.dataset.frame;
    const frameKey = matchFrameKey(frameValue);
    const matOn = isMatOn(selection.mat || this.dataset.mat);

    const artUrl =
      (sizeKey ? this.#config.artworkBySize?.[sizeKey] : null) ||
      SIZE_KEYS.map((key) => this.#config.artworkBySize?.[key]).find(Boolean) ||
      null;
    if (!sizeKey && artUrl) {
      sizeKey = SIZE_KEYS.find((key) => this.#config.artworkBySize?.[key] === artUrl) || null;
    }
    const frames = Array.isArray(this.#config.frames) ? this.#config.frames : [];
    const frame =
      frames.find((f) => matchFrameKey(f.optionMatch) === frameKey) ||
      frames.find((f) => f.isUnframed) ||
      frames[0];

    const stage = this.querySelector('[data-live-mockup-stage]');
    const shell = this.querySelector('[data-live-mockup-frame-shell]');
    const artWrap = this.querySelector('[data-live-mockup-art-wrap]');
    const artEl = this.querySelector('[data-live-mockup-art]');
    const matEl = this.querySelector('[data-live-mockup-mat]');

    if (!(artEl instanceof HTMLImageElement) || !(stage instanceof HTMLElement)) return;

    if (!artUrl) {
      stage.hidden = true;
      this.classList.add('live-mockup--empty');
      this.#syncCaption(null, frameKey, true, false);
      return;
    }

    stage.hidden = false;
    this.classList.remove('live-mockup--empty');

    const isUnframed = Boolean(frame?.isUnframed) || frameKey === 'unframed';
    const useMat = matOn && !isUnframed;
    const overlay = !isUnframed && frame?.overlay ? frame.overlay : null;
    const useCssFrame = !isUnframed && !overlay && frameKey !== 'unframed';

    const sizeInches = this.#config.sizeInches?.[sizeKey] || SIZE_INCHES[sizeKey] || [18, 24];
    const printW = Number(sizeInches[0]) || 18;
    const printH = Number(sizeInches[1]) || 24;
    const matIn = useMat ? MAT_IN : 0;
    const frameIn = isUnframed ? 0 : FRAME_IN;
    const outerW = printW + 2 * matIn + 2 * frameIn;
    const outerH = printH + 2 * matIn + 2 * frameIn;
    const innerW = printW + 2 * matIn;
    const aspect = outerW / outerH;

    this.style.setProperty('--live-mockup-aspect', String(aspect));
    this.style.setProperty('--live-mockup-outer-w-in', String(outerW));
    this.style.setProperty('--live-mockup-outer-h-in', String(outerH));
    this.#frameIn = frameIn;
    this.#outerW = outerW;
    this.#syncInSituLift(sizeKey);
    this.#syncDimensionLabels(outerW, outerH);
    this.#syncCaption(sizeKey, frameKey, isUnframed, useMat, selection.frame || this.dataset.frame);
    if (shell instanceof HTMLElement) {
      this.#syncFramePx(shell);
    } else {
      this.style.setProperty('--frame-width', '0px');
      this.style.setProperty('--frame-rail-length', '0px');
    }
    this.style.setProperty('--mat-width', matIn > 0 ? `calc(100% * ${matIn} / ${innerW})` : '0px');

    artEl.draggable = false;
    artEl.loading = 'eager';
    artEl.fetchPriority = 'high';
    if (artEl.src !== artUrl) {
      artEl.src = artUrl;
    }
    artEl.alt = `${this.#config.productTitle || 'Artwork'} — ${sizeKey || ''}`.trim();

    this.#applyFrame(absoluteUrl(overlay), useCssFrame ? frameKey : '', frameKey);

    if (artWrap instanceof HTMLElement) {
      artWrap.style.inset = '';
    }

    if (matEl instanceof HTMLElement) {
      matEl.hidden = !useMat;
      matEl.style.backgroundColor = frame?.matColor || '#fcfcfc';
    }

    this.dataset.unframed = isUnframed ? 'true' : 'false';
    this.dataset.mat = useMat ? 'yes' : 'no';
    this.dataset.sizeKey = sizeKey || '';
    this.dataset.frameKey = frameKey;

    this.#revealWhenReady(artEl);
  }

  /**
   * Applies the frame finish. Chip and rail tiles are decoded first, then all four sides switch together;
   * the previous frame stays on screen meanwhile. If the chip can't load, falls back to a solid CSS frame.
   * @param {string} url - Absolute chip URL, or '' for a CSS frame / unframed
   * @param {string} cssFrame - Frame key for the solid CSS frame, or '' for none
   * @param {string} frameKey - Frame key used for the CSS fallback when the chip fails
   */
  #applyFrame(url, cssFrame, frameKey) {
    if (url && url === this.#frameUrl) return;
    this.#frameUrl = url;
    const token = ++this.#frameToken;

    if (!url) {
      this.#setFrameMode(cssFrame ? 'css' : 'none', cssFrame);
      this.style.removeProperty('--live-frame-overlay');
      this.#clearRailTiles();
      this.#frameReady = Promise.resolve(true);
      return;
    }

    /** @type {Promise<boolean>} */
    const ready = Promise.all([loadChip(url), loadRailTiles(url)]).then(([chip, tiles]) => {
      if (token !== this.#frameToken) return this.#frameReady;

      if (!chip) {
        this.#frameUrl = null;
        this.style.removeProperty('--live-frame-overlay');
        this.#clearRailTiles();
        this.#setFrameMode('css', frameKey);
        return true;
      }

      this.style.setProperty('--live-frame-overlay', `url("${url}")`);
      if (tiles) {
        this.style.setProperty('--live-frame-overlay-t', `url("${tiles.top}")`);
        this.style.setProperty('--live-frame-overlay-b', `url("${tiles.bottom}")`);
        this.setAttribute('data-rail-tiles', '');
      } else {
        this.#frameUrl = null;
        this.#clearRailTiles();
      }
      this.#setFrameMode('overlay', '');
      return Boolean(tiles);
    });
    this.#frameReady = ready;
  }

  /**
   * @param {'overlay' | 'css' | 'none'} mode
   * @param {string} cssFrame
   */
  #setFrameMode(mode, cssFrame) {
    this.dataset.frameMode = mode;
    const shell = this.querySelector('[data-live-mockup-frame-shell]');
    if (!(shell instanceof HTMLElement)) return;
    if (mode === 'css' && cssFrame) {
      shell.dataset.frame = cssFrame;
    } else {
      delete shell.dataset.frame;
    }
  }

  #clearRailTiles() {
    this.style.removeProperty('--live-frame-overlay-t');
    this.style.removeProperty('--live-frame-overlay-b');
    this.removeAttribute('data-rail-tiles');
  }

  /**
   * @param {string | null} sizeKey
   */
  #syncInSituLift(sizeKey) {
    if (!this.hasAttribute('data-in-situ')) return;
    const host = this.closest('.in-situ');
    if (!(host instanceof HTMLElement)) return;
    const key = sizeKey && SIZE_KEYS.includes(sizeKey) ? sizeKey : '12x18';
    const raw = getComputedStyle(host).getPropertyValue(`--in-situ-lift-${key}`).trim();
    host.style.setProperty('--in-situ-cy-lift', raw || '0%');
  }

  /**
   * @param {number} inches
   * @returns {string}
   */
  #formatInches(inches) {
    const value = Number(inches);
    if (!Number.isFinite(value)) return '';
    const rounded = Math.round(value * 10) / 10;
    const label = Number.isInteger(rounded) ? String(rounded) : String(rounded);
    return `${label}"`;
  }

  /**
   * @param {number} outerW
   * @param {number} outerH
   */
  #syncDimensionLabels(outerW, outerH) {
    const widthEl = this.querySelector('[data-live-mockup-dim-w]');
    const heightEl = this.querySelector('[data-live-mockup-dim-h]');
    if (widthEl instanceof HTMLElement) widthEl.textContent = this.#formatInches(outerW);
    if (heightEl instanceof HTMLElement) heightEl.textContent = this.#formatInches(outerH);
  }

  /**
   * @param {string | null} sizeKey
   * @param {string} frameKey
   * @param {boolean} isUnframed
   * @param {boolean} useMat
   * @param {string} [rawFrame]
   */
  #syncCaption(sizeKey, frameKey, isUnframed, useMat, rawFrame = '') {
    const captions = [];
    const localCaption = this.querySelector('[data-live-mockup-caption]');
    if (localCaption instanceof HTMLElement) captions.push(localCaption);
    const sceneCaption = this.closest('.product-media-container--in-situ')?.querySelector(
      ':scope > [data-live-mockup-caption]'
    );
    if (sceneCaption instanceof HTMLElement && sceneCaption !== localCaption) {
      captions.push(sceneCaption);
    }
    if (!captions.length) return;

    const inches = this.#config?.sizeInches?.[sizeKey] || SIZE_INCHES[sizeKey];
    const printW = Number(inches?.[0]);
    const printH = Number(inches?.[1]);
    const hasSize = Boolean(sizeKey) && Number.isFinite(printW) && Number.isFinite(printH);

    const frameLabel = isUnframed
      ? ''
      : FRAME_CAPTION_LABELS[frameKey] || String(rawFrame || frameKey || '').trim();

    for (const caption of captions) {
      const sizeEl = caption.querySelector('[data-live-mockup-caption-size]');
      const frameEl = caption.querySelector('[data-live-mockup-caption-frame]');

      if (!hasSize) {
        caption.hidden = true;
        if (sizeEl instanceof HTMLElement) sizeEl.textContent = '';
        if (frameEl instanceof HTMLElement) {
          frameEl.textContent = '';
          frameEl.hidden = true;
        }
        continue;
      }

      caption.hidden = false;
      if (sizeEl instanceof HTMLElement) {
        sizeEl.textContent = `${printW}"x${printH}"`;
      }
      if (frameEl instanceof HTMLElement) {
        frameEl.textContent = useMat && frameLabel ? `${frameLabel} with Mat` : frameLabel;
        frameEl.hidden = !frameLabel;
      }
    }
  }
}

if (!customElements.get('live-product-mockup')) {
  customElements.define('live-product-mockup', LiveProductMockup);
}
